import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userVerified, setUserVerified] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, userData, role) {
    try {
      console.log("Attempting signup with:", email, "role:", role);
      console.log("User data:", userData);
      
      // Coba buat akun dengan Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("Firebase Auth signup successful, user ID:", user.uid);
      
      try {
        // Kirim email verifikasi
        await sendEmailVerification(user);
        console.log("Verification email sent");
        
        // Pastikan userData memiliki semua properti yang diperlukan
        const completeUserData = {
          nama: userData.nama || email.split('@')[0],
          email,
          role,
          verified: role === 'pj' ? true : false, // PJ langsung terverifikasi, yang lain perlu diverifikasi
          createdAt: new Date().toISOString()
        };
        
        console.log("Setting user verification status:", completeUserData.verified);
        
        // Tambahkan properti lain dari userData jika ada
        if (userData) {
          Object.keys(userData).forEach(key => {
            if (key !== 'nama' && key !== 'email' && key !== 'role' && key !== 'verified' && key !== 'createdAt') {
              completeUserData[key] = userData[key];
            }
          });
        }
        
        console.log("Complete user data to save:", completeUserData);
        
        // Simpan data tambahan pengguna ke Firestore Database
        console.log("Saving user data to Firestore Database for user ID:", user.uid);
        const userRef = doc(db, "users", user.uid);
        await setDoc(userRef, completeUserData);
        
        // Verifikasi data tersimpan dengan membacanya kembali
        const savedDoc = await getDoc(userRef);
        if (savedDoc.exists()) {
          console.log("User data saved and verified successfully:", savedDoc.data());
          return { success: true };
        } else {
          throw new Error("Data pengguna tidak tersimpan dengan benar");
        }
      } catch (dataError) {
        // Jika gagal menyimpan data, hapus akun yang baru dibuat
        console.error("Error saving user data:", dataError);
        try {
          await user.delete();
          console.log("User account deleted due to data save error");
        } catch (deleteError) {
          console.error("Error deleting user account:", deleteError);
        }
        return { success: false, error: "Terjadi kesalahan saat menyimpan data pengguna: " + dataError.message };
      }
    } catch (error) {
      console.error("Signup error:", error);
      // Tangani error email sudah ada
      if (error.code === 'auth/email-already-in-use') {
        return { success: false, error: "Email sudah terdaftar. Silakan gunakan email lain atau login dengan email ini." };
      }
      return { success: false, error: error.message };
    }
  }

  async function login(email, password) {
    try {
      console.log("Attempting login with:", email);
      
      // Login dengan Firebase Authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("Firebase Auth login successful, user ID:", user.uid);
      
      try {
        // Log database reference untuk debugging
        console.log("Firestore document path: users/", user.uid);
        
        // Periksa apakah pengguna sudah diverifikasi oleh PJ
        const userRef = doc(db, "users", user.uid);
        console.log("User reference:", userRef);
        
        const userDoc = await getDoc(userRef);
        console.log("User document exists:", userDoc.exists());
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log("User data:", userData);
          console.log("User role:", userData.role, "Verified:", userData.verified);
          
          // Set user role dan status verifikasi
          setUserRole(userData.role);
          setUserVerified(userData.verified);
          setUserData(userData);
          
          // Periksa apakah akun sudah diverifikasi
          if (!userData.verified) {
            console.log("User not verified, blocking login");
            return { success: false, error: "Akun Anda belum diverifikasi oleh Penanggung Jawab Tugas Akhir" };
          }
          
          console.log("Login successful");
          return { success: true };
        } else {
          console.log("User data not found, signing out");
          
          // Coba buat data pengguna jika tidak ada
          try {
            console.log("Attempting to create user data for ID:", user.uid);
            
            // Dapatkan email dari user auth
            const email = user.email;
            
            // Buat data pengguna dasar
            await setDoc(doc(db, "users", user.uid), {
              email: email,
              role: 'pj', // Default sebagai admin untuk memastikan bisa login
              nama: email.split('@')[0], // Gunakan bagian depan email sebagai nama
              verified: true,
              createdAt: new Date().toISOString()
            });
            
            console.log("Created basic user data");
            setUserRole('pj');
            setUserVerified(true);
            setUserData({ email, role: 'pj', nama: email.split('@')[0], verified: true, createdAt: new Date().toISOString() });
            
            return { success: true };
          } catch (createError) {
            console.error("Failed to create user data:", createError);
            await signOut(auth);
            return { success: false, error: "Gagal membuat data pengguna: " + createError.message };
          }
        }
      } catch (dbError) {
        console.error("Error checking user data:", dbError);
        await signOut(auth);
        return { success: false, error: "Terjadi kesalahan saat memeriksa data pengguna: " + dbError.message };
      }
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: error.message };
    }
  }

  function logout() {
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function fetchUserData(uid) {
    try {
      console.log("Fetching user data for UID:", uid);
      const userDoc = await getDoc(doc(db, "users", uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log("User data found:", userData);
        setUserRole(userData.role);
        setUserVerified(userData.verified);
        setUserData(userData);
        return userData;
      } else {
        console.log("No user document found for UID:", uid);
        // Jika tidak ada dokumen pengguna, buat dokumen dasar untuk pengguna
        try {
          const user = auth.currentUser;
          if (user && user.email) {
            console.log("Creating basic user data for UID:", uid);
            // Untuk keamanan, jika perlu membuat data pengguna baru,
            // kita akan membuat mereka sebagai 'mahasiswa' yang belum diverifikasi
            // kecuali jika email domain menunjukkan mereka adalah admin
            const isAdmin = user.email.endsWith('@admin.com') || user.email.endsWith('@rekos.ac.id');
            
            const basicUserData = {
              email: user.email,
              role: isAdmin ? 'pj' : 'mahasiswa',
              nama: user.email.split('@')[0],
              verified: isAdmin, // Hanya admin yang otomatis terverifikasi
              createdAt: new Date().toISOString()
            };
            
            console.log("Creating user with role:", basicUserData.role, "and verification status:", basicUserData.verified);
            
            await setDoc(doc(db, "users", uid), basicUserData);
            console.log("Basic user data created successfully");
            
            setUserRole(basicUserData.role);
            setUserVerified(basicUserData.verified);
            setUserData(basicUserData);
            return basicUserData;
          }
        } catch (createError) {
          console.error("Error creating basic user data:", createError);
        }
      }
      return null;
    } catch (error) {
      console.error("Error fetching user data:", error);
      return null;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserData(user.uid);
      } else {
        setUserRole(null);
        setUserVerified(false);
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    userVerified,
    userData,
    signup,
    login,
    logout,
    resetPassword,
    fetchUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
